export type Currency = 'USD' | 'SYP';
export type Kind = 'income' | 'expense' | 'exchange';

export const CATEGORIES = ['Food', 'Groceries', 'Transport', 'Bills', 'Other'] as const;
export type Category = typeof CATEGORIES[number];

export interface Entry {
  id: number;
  user_id: number;
  occurred_at: string;
  currency: Currency;
  amount: string; // numeric from postgres serialized as string
  kind: Kind;
  category: string | null;
  note: string | null;
  exchange_id: string | null;
  rate: string | null;
  photo_url: string | null;
  created_at: string;
}

export interface Budget {
  user_id: number;
  category: string;
  currency: Currency;
  monthly_limit: string | number;
  spent_this_month?: number;
}

export interface Debt {
  id: number;
  user_id: number;
  direction: 'owed_to_me' | 'i_owe';
  counterparty: string;
  currency: Currency;
  amount: string | number;
  note: string | null;
  status: 'open' | 'settled';
  created_at: string;
  settled_at: string | null;
}

export interface Goal {
  id: number;
  user_id: number;
  name: string;
  currency: Currency;
  target_amount: string | number;
  saved_amount: string | number;
  created_at: string;
  achieved_at: string | null;
}

export interface Summary {
  balances: Record<Currency, number>;
  month_spend: Record<Currency, number>;
  month_income: Record<Currency, number>;
  latest_rate_syp_per_usd: number | null;
}

export interface Preset {
  id: number;
  user_id: number;
  label: string;
  emoji: string | null;
  currency: Currency;
  amount: string | number;
  kind: 'income' | 'expense';
  category: string | null;
  note: string | null;
  sort_order: number;
}

export interface Recurring {
  id: number;
  user_id: number;
  label: string;
  currency: Currency;
  amount: string | number;
  kind: 'income' | 'expense';
  category: string | null;
  note: string | null;
  day_of_month: number;
  active: boolean;
  last_run_for: string | null;
}

export interface CategoryRow {
  category: string;
  amount: number;
  percent: number;
}

export interface DailyPoint {
  date: string;
  USD: number;
  SYP: number;
}

export interface UserSettings {
  user_id: number;
  reminder_enabled: boolean;
  reminder_time_local: string | null;
  tz_offset_minutes: number;
  snoozed_until: string | null;
  last_reminder_sent_at: string | null;
}

export interface Reports {
  balances: Record<Currency, number>;
  days_active: number;
  this_month: {
    income:  Record<Currency, number>;
    expense: Record<Currency, number>;
    net:     Record<Currency, number>;
    days_elapsed: number;
    days_in_month: number;
  };
  last_month: {
    income:  Record<Currency, number>;
    expense: Record<Currency, number>;
  };
  adjustments_this_month: Record<Currency, number>;
  by_category: Record<Currency, CategoryRow[]>;
  daily_spend_last_30: DailyPoint[];
  monthly_trend: { month: string; USD: number; SYP: number }[];
  insights: Record<Currency, string[]>;
}
